/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { ReactNode, useEffect, useState } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Pagination, Typography } from "@mui/material";
import MDPagination from "components/MDPagination";

// Declaring props types for CategoriesList
interface Props {
  title?: string;
  colorHexa?: string;
  itemsPerPage?: number;
  categories: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    icon: ReactNode | string;
    name: string;
    description: ReactNode;
    route: string;
  }[];
  [key: string]: any;
}

function CategoriesList({ title, categories, colorHexa, itemsPerPage }: Props): JSX.Element {
  const [page, setPage] = useState(1);
  const totalPages = itemsPerPage ? Math.ceil(categories.length / itemsPerPage) : 1;

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const visibleItems = itemsPerPage
    ? categories.slice((page - 1) * itemsPerPage, page * itemsPerPage)
    : categories;

  const renderItems = visibleItems.map(({ color, icon, name, description, route }, key) => (
    <MDBox
      key={`${name}-${(page - 1) * (itemsPerPage ?? 0) + key}`}
      component="li"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      borderRadius="lg"
      py={1}
      pr={2}
      mb={visibleItems.length - 1 === key ? 0 : 1}
    >
      <MDBox display="flex" alignItems="center">
        <MDBox
          display="grid"
          alignItems="center"
          justifyContent="center"
          bgColor={color}
          borderRadius="lg"
          shadow="md"
          color="white"
          width="2rem"
          height="2rem"
          mr={2}
          variant="gradient"
          fontSize="0.875rem"
          sx={{ flexShrink: 0, background: colorHexa }}
        >
          <Icon
            sx={{
              display: "grid",
              placeItems: "center",
            }}
          >
            {icon}
          </Icon>
        </MDBox>
        <MDBox display="flex" flexDirection="column">
          <Typography variant="body2" fontWeight="medium">
            {name}
          </Typography>
          <MDTypography variant="caption" color="text">
            {description}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox display="flex">
        <MDTypography
          component={Link}
          variant="button"
          color={color}
          to={route}
          sx={{
            lineHeight: 0,
            transition: "all 0.2s cubic-bezier(.34,1.61,.7,1.3)",
            p: 0.5,

            "&:hover, &:focus": {
              transform: "translateX(5px)",
            },
          }}
        >
          <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
        </MDTypography>
      </MDBox>
    </MDBox>
  ));

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <MDBox pt={2} px={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {title}
        </MDTypography>
      </MDBox>
      <MDBox p={2} flexGrow={1}>
        <MDBox component="ul" display="flex" flexDirection="column" p={0} m={0}>
          {renderItems}
        </MDBox>
      </MDBox>

      {totalPages > 1 && (
        <MDBox display="flex" justifyContent="center" alignItems="center" pb={2}>
          <MDPagination variant="gradient" color="dark" size="small">
            <MDPagination item disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
            </MDPagination>

            <MDTypography variant="button" color="text" mx={1}>
              {page} / {totalPages}
            </MDTypography>

            <MDPagination
              item
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDPagination>
          </MDPagination>
        </MDBox>
      )}
    </Card>
  );
}

export default CategoriesList;
